/**
 * MemberCycleProofOfWorkSubpage — Patch v16, Item 4.
 *
 * Two paginated tables, 50 rows / page, newest first:
 *
 *   List A — Personal Cycles & Fail-Safe Ledger
 *     col 1: Cycled (always ✓ — row exists ⇒ cycle happened)
 *     col 2: Auto-Rebuys (✓ if the 2 system-generated re-entry
 *            positions spawned successfully via either the inline path
 *            or the async retry; ✗ + "Claim Rebuys" button if the
 *            pending_snowballs entry is still pending)
 *
 *   List B — Referral Matching Bonus Ledger
 *     col 1: Referral username
 *     col 2: Status (✓ if paid; ✗ + "Process Bonus" button if pending)
 *
 * The two manual buttons share the same idempotent backend primitive as
 * the 30-second background retry workers — a member spam-click is safe
 * (second click returns 409 ALREADY_PROCESSED).
 */

import { AlertTriangle, Check, ChevronLeft, ChevronRight, Hourglass, Loader2, RefreshCw, TimerReset, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { claimCycleRebuys, fetchCycleProofPending, fetchCycleProofPersonal, fetchCycleProofReferrals, processCycleBonus } from '@/api/member';

const PER_PAGE = 50;

function shortIso(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/* Patch v17.4 — Pending Positions panel uses a compact age display so
 * members can see at-a-glance how long each pending position has been
 * waiting in the pool. Returns a short ASCII string like "3h 12m" or
 * "5d 4h" — graceful fallback "—" if the input is missing/invalid. */
function ageFromIso(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remM = mins % 60;
    if (hours < 24) return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
  } catch {
    return '—';
  }
}

/* Patch v17.4 — Source badge palette mirrors the existing admin Proof
 * of Work selection-log palette so a member can recognize their
 * position's funding origin at a glance.
 *   single     — direct out-of-pocket purchase ($1 from balance)
 *   subpack    — automated drip release from an active Sub Pack
 *   rebuy      — cycle-driven snowball auto-rebuy (the 2-spawn)
 *   auto_rebuy — auto-repurchase reservoir drain (the 5-spawn)
 *   unknown    — legacy / pre-v10 entry */
const SOURCE_BADGE = {
  single: { label: 'SINGLE', bg: 'rgba(96,165,250,0.10)', fg: '#60A5FA', border: 'rgba(96,165,250,0.40)' },
  subpack: { label: 'SUB PACK', bg: 'rgba(52,211,153,0.10)', fg: '#34D399', border: 'rgba(52,211,153,0.40)' },
  rebuy: { label: 'Legacy Snowball', bg: 'rgba(196,181,253,0.10)', fg: '#C4B5FD', border: 'rgba(196,181,253,0.40)' },
  auto_rebuy: { label: 'AUTO-REBUY', bg: 'rgba(255,194,14,0.10)', fg: '#FFC20E', border: 'rgba(255,194,14,0.45)' },
  unknown: { label: 'UNKNOWN', bg: 'rgba(160,160,160,0.10)', fg: '#A0A0A0', border: 'rgba(160,160,160,0.30)' },
};

const SourceBadge = ({ value, testid }) => {
  const meta = SOURCE_BADGE[value] || SOURCE_BADGE.unknown;
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
      style={{
        background: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.border}`,
      }}
      data-testid={testid}
    >
      {meta.label}
    </span>
  );
};

const PhaseBadge = ({ value, testid }) => {
  const isRoot = value === 'root';
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
      style={{
        background: isRoot ? 'rgba(255,194,14,0.14)' : 'rgba(255,194,14,0.05)',
        color: isRoot ? '#FFC20E' : 'rgba(255,194,14,0.75)',
        border: `1px solid ${isRoot ? 'rgba(255,194,14,0.55)' : 'rgba(255,194,14,0.25)'}`,
      }}
      data-testid={testid}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{
          background: isRoot ? '#FFC20E' : 'rgba(255,194,14,0.55)',
          boxShadow: isRoot ? '0 0 6px rgba(255,194,14,0.65)' : 'none',
        }}
      />
      {isRoot ? 'ROOT' : 'FILLER'}
    </span>
  );
};

const MemberCycleProofOfWorkSubpage = () => {
  return (
    <div className="space-y-8" data-testid="cycle-pow-page">
      <header className="space-y-1">
        <div className="inline-flex items-center gap-2">
          <span className="sc-pill">
            <span className="sc-dot" />
            Cycle Proof of Work
          </span>
        </div>
        <h1 className="font-display font-bold text-2xl md:text-3xl mt-2">
          Your <span className="sc-gold-text">cycle audit trail</span>
        </h1>
        <p className="text-[13px] text-[var(--sc-text-dim)] max-w-3xl">
          Every cycle your positions complete + every sponsor matching bonus you earn. Unpaid bonuses show a red X with a one-click manual recovery button. The
          platform's 30-second background retry workers also handle these automatically — the buttons just let you force a retry on demand. Snowball auto-rebuys
          are disabled as of Patch v17.5 (all $3 per cycle now distributes to real accounts, no reinvestment).
        </p>
      </header>

      <PendingPositionsPanel />
      <PersonalCyclesPanel />
      <ReferralBonusPanel />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Patch v17.4 — Pending Positions Panel (NEW)
// ---------------------------------------------------------------------------
// Renders ABOVE List A. One paginated, filterable list of the member's
// pending (not-yet-cycled) positions, drawn live from the pending_filler +
// pending_root pools via the new /api/me/cycle-pow/pending endpoint.
//
// Combined-list design (a deliberate choice over two separate lists):
//   * A filler→root promotion is visually clear — the badge flips colour
//     in place rather than disappearing from one list and reappearing in
//     another.
//   * Single 50/page cursor; UX is consistent with the existing Lists A/B.
//   * Filter chips ("All / Filler / Root") drive a single backend query;
//     the badge counts in the chips reflect the member's full pending set
//     regardless of which filter is active.
const PendingPositionsPanel = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [phase, setPhase] = useState('all'); // all | filler | root
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (p, ph) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchCycleProofPending(p, PER_PAGE, ph);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || 'Failed to load pending positions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, phase);
  }, [page, phase, load]);

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / PER_PAGE)) : 1;

  const counts = data?.filter_counts || { all: 0, filler: 0, root: 0 };

  const chip = (key, label) => {
    const active = phase === key;
    const count = counts[key] ?? 0;
    return (
      <button
        type="button"
        onClick={() => {
          setPhase(key);
          setPage(1);
        }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.18em] border transition-colors"
        style={
          active
            ? {
                background: 'rgba(255,194,14,0.14)',
                borderColor: 'rgba(255,194,14,0.55)',
                color: '#FFC20E',
              }
            : {
                background: 'rgba(0,0,0,0.34)',
                borderColor: 'rgba(255,255,255,0.08)',
                color: 'var(--sc-text-mute)',
              }
        }
        data-testid={`cycle-pow-pending-chip-${key}`}
      >
        {label}
        <span
          className="sc-tabular px-1.5 rounded-full text-[10px]"
          style={{
            background: active ? 'rgba(0,0,0,0.32)' : 'rgba(255,255,255,0.04)',
            color: active ? '#FFC20E' : 'var(--sc-text-dim)',
          }}
        >
          {count.toLocaleString()}
        </span>
      </button>
    );
  };

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="cycle-pow-list-pending">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Hourglass className="w-4 h-4 text-[#FFC20E]" />
            Pending Positions — Awaiting Cycle
          </h2>
          <p className="text-[12px] text-[var(--sc-text-dim)] mt-1 max-w-2xl">
            Every position you own that is still in the live filler or root pools. A position starts as a filler, gets promoted to root when selected as part of
            a cycle's 3-filler slice, and finally cycles — at which point it disappears from this list and surfaces in the Personal Cycles ledger below.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(page, phase)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] disabled:opacity-50"
          data-testid="cycle-pow-pending-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </header>

      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="cycle-pow-pending-filter-chips">
        {chip('all', 'All')}
        {chip('filler', 'Filler')}
        {chip('root', 'Root')}
      </div>

      {error ? (
        <div className="mt-4 px-3 py-2 rounded-lg bg-[rgba(255,99,71,0.08)] border border-[rgba(255,99,71,0.32)] text-[12px] text-[#FF6347]">{error}</div>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-xl border border-[rgba(255,255,255,0.06)]" data-testid="cycle-pow-pending-table-wrap">
        <table className="w-full text-[12px] text-left">
          <thead>
            <tr style={{ color: 'var(--sc-text-mute)', background: 'rgba(255,255,255,0.03)' }}>
              <Th>Position ID</Th>
              <Th>Source</Th>
              <Th>Phase</Th>
              <Th>Purchased</Th>
              <Th align="right">Age</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (!data || (data.rows || []).length === 0) ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            ) : !data || (data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  No pending positions. Every position you own has either cycled or you haven&rsquo;t purchased any yet.
                </td>
              </tr>
            ) : (
              data.rows.map((r, i) => (
                <tr key={r.position_id || i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }} data-testid={`cycle-pow-pending-row-${i}`}>
                  <Td className="font-mono text-[var(--sc-text-dim)]">
                    {(r.position_id || '').slice(0, 8)}
                    {(r.position_id || '').length > 8 ? '…' : ''}
                  </Td>
                  <Td>
                    <SourceBadge value={r.source} testid={`cycle-pow-pending-source-${i}`} />
                  </Td>
                  <Td>
                    <PhaseBadge value={r.phase} testid={`cycle-pow-pending-phase-${i}`} />
                  </Td>
                  <Td className="sc-tabular text-[var(--sc-text-dim)]">{shortIso(r.purchase_created_at)}</Td>
                  <Td align="right" className="sc-tabular text-[var(--sc-text-dim)]">
                    {ageFromIso(r.purchase_created_at)}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data ? (
        <Pagination
          page={data.page}
          total={data.total}
          perPage={data.per_page || PER_PAGE}
          totalPages={totalPages}
          onChange={setPage}
          disabled={loading}
          testIdPrefix="cycle-pow-pending"
        />
      ) : null}
    </section>
  );
};

// ---------------------------------------------------------------------------
// List A — Personal Cycles
// ---------------------------------------------------------------------------
const PersonalCyclesPanel = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimBusy, setClaimBusy] = useState({}); // { [position_id]: true }

  const load = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchCycleProofPersonal(p, PER_PAGE);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || 'Failed to load your cycles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [page, load]);

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / PER_PAGE)) : 1;

  const onClaim = async (positionId) => {
    setClaimBusy((b) => ({ ...b, [positionId]: true }));
    try {
      const r = await claimCycleRebuys(positionId);
      toast.success('Auto-rebuys claimed — 2 positions queued.');
      // Optimistically flip the row to ✓ in local state.
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((row) =>
                row.position_id === positionId
                  ? {
                      ...row,
                      auto_rebuy_status: 'ok',
                      claimable_rebuys: false,
                      retry_completed_via: r?.via || 'manual_claim',
                    }
                  : row,
              ),
            }
          : d,
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || e?.message || 'Claim failed.';
      toast.error(`Claim failed: ${msg}`);
      // Refresh on conflict so the UI matches server reality.
      if (e?.response?.status === 409 || e?.response?.status === 404) {
        load(page);
      }
    } finally {
      setClaimBusy((b) => ({ ...b, [positionId]: false }));
    }
  };

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="cycle-pow-list-a">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <TimerReset className="w-4 h-4 text-[#FFC20E]" />
            Personal Cycles & Fail-Safe Ledger
          </h2>
          <p className="text-[12px] text-[var(--sc-text-dim)] mt-1 max-w-2xl">
            Each row is one cycle one of your positions completed. The Auto-Rebuys column tracks legacy pre-v17.5 snowball auto- rebuys (2 per cycle). As of
            Patch v17.5 snowballs are disabled — the full $3.00 collected per cycle is distributed to real accounts ($2.50 cycler + $0.25 sponsor + $0.25 admin)
            with zero reinvestment. Historical rows continue to show their original ✓ / ✗ status; new cycles show —.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(page)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] disabled:opacity-50"
          data-testid="cycle-pow-list-a-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </header>

      {error ? (
        <div className="mt-4 px-3 py-2 rounded-lg bg-[rgba(255,99,71,0.08)] border border-[rgba(255,99,71,0.32)] text-[12px] text-[#FF6347]">{error}</div>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-xl border border-[rgba(255,255,255,0.06)]" data-testid="cycle-pow-list-a-table-wrap">
        <table className="w-full text-[12px] text-left">
          <thead>
            <tr style={{ color: 'var(--sc-text-mute)', background: 'rgba(255,255,255,0.03)' }}>
              <Th>Cycled At</Th>
              <Th>Source</Th>
              <Th align="center">Cycled</Th>
              <Th align="center">Auto-Rebuys</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (!data || (data.rows || []).length === 0) ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            ) : !data || (data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  No cycles yet. Buy your first position to start.
                </td>
              </tr>
            ) : (
              data.rows.map((r, i) => {
                const claimable = Boolean(r.claimable_rebuys);
                const status = r.auto_rebuy_status;
                return (
                  <tr key={r.position_id || i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }} data-testid={`cycle-pow-list-a-row-${i}`}>
                    <Td className="sc-tabular text-[var(--sc-text-dim)]">{shortIso(r.cycled_at)}</Td>
                    <Td>
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
                        style={{
                          background: 'rgba(255,182,18,0.08)',
                          color: '#FFC20E',
                          border: '1px solid rgba(255,182,18,0.22)',
                        }}
                      >
                        {r.source || 'unknown'}
                      </span>
                    </Td>
                    <Td align="center">
                      <Check className="w-4 h-4 text-emerald-400 inline" data-testid={`cycle-pow-list-a-cycled-${i}`} />
                    </Td>
                    <Td align="center">
                      {status === 'ok' ? (
                        <Check className="w-4 h-4 text-emerald-400 inline" data-testid={`cycle-pow-list-a-rebuys-ok-${i}`} />
                      ) : status === 'processing' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#FFC20E]" data-testid={`cycle-pow-list-a-rebuys-processing-${i}`}>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          processing
                        </span>
                      ) : status === 'abandoned' ? (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] text-[var(--sc-text-mute)]"
                          data-testid={`cycle-pow-list-a-rebuys-abandoned-${i}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          abandoned
                        </span>
                      ) : (
                        <X className="w-4 h-4 text-[#FF6347] inline" data-testid={`cycle-pow-list-a-rebuys-fail-${i}`} />
                      )}
                    </Td>
                    <Td align="right">
                      {claimable ? (
                        <button
                          type="button"
                          onClick={() => onClaim(r.position_id)}
                          disabled={!!claimBusy[r.position_id]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                          style={{
                            background: 'rgba(255,99,71,0.12)',
                            border: '1px solid rgba(255,99,71,0.55)',
                            color: '#FF6347',
                          }}
                          data-testid={`cycle-pow-list-a-claim-btn-${i}`}
                        >
                          {claimBusy[r.position_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />}
                          Claim Rebuys
                        </button>
                      ) : null}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data ? (
        <Pagination
          page={data.page}
          total={data.total}
          perPage={data.per_page || PER_PAGE}
          totalPages={totalPages}
          onChange={setPage}
          disabled={loading}
          testIdPrefix="cycle-pow-list-a"
        />
      ) : null}
    </section>
  );
};

// ---------------------------------------------------------------------------
// List B — Referral Matching Bonus
// ---------------------------------------------------------------------------
const ReferralBonusPanel = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bonusBusy, setBonusBusy] = useState({});

  const load = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchCycleProofReferrals(p, PER_PAGE);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || 'Failed to load your referral matches.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [page, load]);

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / PER_PAGE)) : 1;

  const onProcess = async (positionId) => {
    setBonusBusy((b) => ({ ...b, [positionId]: true }));
    try {
      await processCycleBonus(positionId);
      toast.success('Sponsor bonus paid.');
      setData((d) =>
        d
          ? {
              ...d,
              rows: d.rows.map((row) =>
                row.position_id === positionId
                  ? {
                      ...row,
                      status: 'paid',
                      claimable: false,
                      completed_via: 'manual_claim',
                    }
                  : row,
              ),
            }
          : d,
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || e?.message || 'Process failed.';
      toast.error(`Process failed: ${msg}`);
      if (e?.response?.status === 409 || e?.response?.status === 404) {
        load(page);
      }
    } finally {
      setBonusBusy((b) => ({ ...b, [positionId]: false }));
    }
  };

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="cycle-pow-list-b">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#FFC20E]" />
            Referral Matching Bonus Ledger
          </h2>
          <p className="text-[12px] text-[var(--sc-text-dim)] mt-1 max-w-2xl">
            Every cycle one of your direct referrals completed generates a matching bonus for you. Failed payouts show a red X with the "Process Bonus" button —
            clicking it pays the bonus.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(page)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] disabled:opacity-50"
          data-testid="cycle-pow-list-b-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </header>

      {error ? (
        <div className="mt-4 px-3 py-2 rounded-lg bg-[rgba(255,99,71,0.08)] border border-[rgba(255,99,71,0.32)] text-[12px] text-[#FF6347]">{error}</div>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-xl border border-[rgba(255,255,255,0.06)]" data-testid="cycle-pow-list-b-table-wrap">
        <table className="w-full text-[12px] text-left">
          <thead>
            <tr style={{ color: 'var(--sc-text-mute)', background: 'rgba(255,255,255,0.03)' }}>
              <Th>Date</Th>
              <Th>Referral</Th>
              <Th align="right">Bonus</Th>
              <Th align="center">Status</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (!data || (data.rows || []).length === 0) ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            ) : !data || (data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[var(--sc-text-mute)]">
                  No referral matches yet.
                </td>
              </tr>
            ) : (
              data.rows.map((r, i) => {
                const claimable = Boolean(r.claimable);
                const isPaid = r.status === 'paid';
                const isAbandoned = r.status === 'abandoned';
                const isProcessing = r.status === 'processing';
                return (
                  <tr key={r.position_id || i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }} data-testid={`cycle-pow-list-b-row-${i}`}>
                    <Td className="sc-tabular text-[var(--sc-text-dim)]">{shortIso(r.event_at)}</Td>
                    <Td>
                      <span className="text-[var(--sc-text)]">@{r.sponsored_username || 'unknown'}</span>
                    </Td>
                    <Td align="right" className="sc-tabular">
                      ${Number(r.amount_usd || 0).toFixed(2)}
                    </Td>
                    <Td align="center">
                      {isPaid ? (
                        <Check className="w-4 h-4 text-emerald-400 inline" data-testid={`cycle-pow-list-b-paid-${i}`} />
                      ) : isProcessing ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#FFC20E]" data-testid={`cycle-pow-list-b-processing-${i}`}>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          processing
                        </span>
                      ) : isAbandoned ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[var(--sc-text-mute)]" data-testid={`cycle-pow-list-b-abandoned-${i}`}>
                          <AlertTriangle className="w-3 h-3" />
                          abandoned
                        </span>
                      ) : (
                        <X className="w-4 h-4 text-[#FF6347] inline" data-testid={`cycle-pow-list-b-fail-${i}`} />
                      )}
                    </Td>
                    <Td align="right">
                      {claimable ? (
                        <button
                          type="button"
                          onClick={() => onProcess(r.position_id)}
                          disabled={!!bonusBusy[r.position_id]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                          style={{
                            background: 'rgba(255,99,71,0.12)',
                            border: '1px solid rgba(255,99,71,0.55)',
                            color: '#FF6347',
                          }}
                          data-testid={`cycle-pow-list-b-process-btn-${i}`}
                        >
                          {bonusBusy[r.position_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />}
                          Process Bonus
                        </button>
                      ) : null}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data ? (
        <Pagination
          page={data.page}
          total={data.total}
          perPage={data.per_page || PER_PAGE}
          totalPages={totalPages}
          onChange={setPage}
          disabled={loading}
          testIdPrefix="cycle-pow-list-b"
        />
      ) : null}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Tiny presentational helpers (kept local — used only by this subpage)
// ---------------------------------------------------------------------------
const Th = ({ children, align = 'left' }) => (
  <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold" style={{ textAlign: align, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    {children}
  </th>
);

const Td = ({ children, align = 'left', className = '' }) => (
  <td className={`px-3 py-2 ${className}`} style={{ textAlign: align, color: 'var(--sc-text)' }}>
    {children}
  </td>
);

const Pagination = ({ page, total, perPage, totalPages, onChange, disabled, testIdPrefix }) => {
  const first = total === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(total, page * perPage);
  return (
    <div className="mt-3 flex items-center justify-between text-[11px] text-[var(--sc-text-mute)]" data-testid={`${testIdPrefix}-pagination`}>
      <span>{total === 0 ? '0 rows' : `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || page <= 1}
          onClick={() => onChange(page - 1)}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border disabled:opacity-40"
          style={{
            background: 'rgba(0,0,0,0.34)',
            borderColor: 'rgba(255,255,255,0.10)',
            color: 'var(--sc-text)',
          }}
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="w-3 h-3" />
          Prev
        </button>
        <span className="sc-tabular">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={disabled || page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border disabled:opacity-40"
          style={{
            background: 'rgba(0,0,0,0.34)',
            borderColor: 'rgba(255,255,255,0.10)',
            color: 'var(--sc-text)',
          }}
          data-testid={`${testIdPrefix}-next`}
        >
          Next
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default MemberCycleProofOfWorkSubpage;
